import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, useMediaQuery, View } from 'native-base';
import { useBoolean } from 'usehooks-ts';
import FeatherIcons from 'react-native-vector-icons/Feather';
import { ArrowBackIcon, ArrowForwardIcon, HStack } from 'native-base';
import { isEmpty, range } from 'lodash';
import moment from 'moment';

import { Button, ButtonGroup, Text, SwitchButton } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { formatToISOString } from '@app/utils/date-formatter';
import { MainContainer } from '@app/components/core';
import { ScreenLayout } from '@app/components/layout';
import { ResourceLoader } from '@app/components/shared';
import { ModuleTitle } from '@app/features/app';
import { DateTimePopover, Daypart, useSiteConfig, useSiteTime } from '@app/features/store-core';
import { ImporterConnection } from '@app/features/sales-tracker-v2/components/importer-connection';
import { SalesLaborTrackerProvider } from '@app/features/sales-tracker-v2/context/sales-labor-tracker.context';
import { useGetSalesAndLabor } from '@app/features/sales-tracker-v2/hooks/get-sales-and-labor.hook';
import { SalesTracker } from '@app/features/sales-tracker-v2/components/sales-tracker';
import { OperationalHoursTab } from '@app/features/sales-tracker-v2/components/operational-hours-tab';
import { MainScreenProps } from '@app/navigations/root';

export type SalesTrackerScreenProps = MainScreenProps<'SalesTracker'>;

// TODO: need to rewrite offsettime implementation in here
export const SalesTrackerScreen: React.FC<SalesTrackerScreenProps> = ({ route }) => {
  const { t } = useTranslation();
  const { locale } = useAppLocale();
  const { siteConfig } = useSiteConfig();
  const siteId = siteConfig.id;
  const siteTime = useSiteTime();
  const isDetailed = useBoolean();
  const [isSmallScreen] = useMediaQuery({ maxWidth: 1200 });
  const [dayparts, setDayparts] = useState<Daypart[]>();
  const [week, setWeek] = useState<ReturnType<typeof siteTime.toStartEndOfWeekPart>>();
  const maxDate = siteTime.toStartEndOfWeekPart().endTime;

  const querySalesAndLabor = useGetSalesAndLabor({ siteId, date: dayparts?.[0]?.startTime });

  const dateFocus = dayparts?.length ? formatToISOString(dayparts[0]?.startTime) : undefined;

  const handleChangeDate = (date: string) => {
    const _dayparts = siteTime.toDayparts(new Date(date));
    setDayparts(_dayparts);
  };

  const handleSelectWeek = date => {
    const today = siteTime.today();
    const week = siteTime.toStartEndOfWeek(date);
    let dayparts = siteTime.toDayparts(week.endDate);

    if (dayparts[0].startTime && dayparts[0].startTime > today) {
      dayparts = siteTime.toDayparts();
    }

    setDayparts(dayparts);
    setWeek(siteTime.toStartEndOfWeekPart(date));
  };

  const handleChangeWeek = (days?: number) => () => {
    if (week) {
      let startOfWeek = siteTime.toStartEndOfWeek().startDate;
      if (days) {
        startOfWeek = moment(week.startTime).add(days, 'days').toDate();
      }
      handleSelectWeek(startOfWeek);
    }
  };

  useEffect(() => {
    if (!siteTime.siteId) return;
    setDayparts(siteTime.toDayparts());
    setWeek(siteTime.toStartEndOfWeekPart());
  }, [siteTime.siteId]);

  return (
    <MainContainer>
      <SalesLaborTrackerProvider key={dayparts?.[0]?.startTime.toISOString()} dayparts={dayparts} querySalesAndLabor={querySalesAndLabor}>
        <ScreenLayout
          title={<ModuleTitle name={route.name} defaultText="Sales & Labor" />}
          actionTool={
            <HStack space={1.5} alignItems="center" justifyContent="flex-end" flexWrap="wrap">
              <Box my={0.5}>
                <OperationalHoursTab />
              </Box>

              <Box my={0.5}>
                <HStack space={1.5} rounded="full" alignItems="center" pl={1.5} pr={2.5} h={7} bg="gray.100">
                  <SwitchButton size="3xl" isChecked={isDetailed.value} onChange={isDetailed.setValue} />
                  <Text size="md" fontWeight={600} color="gray.600" lineHeight="md">
                    {isDetailed.value ? t('sales_tracker:detail') : t('sales_tracker:manager')}
                  </Text>
                </HStack>
              </Box>

              {!!querySalesAndLabor.data?.syncInfo && (
                <Box my={0.5}>
                  <ImporterConnection syncInfo={querySalesAndLabor.data?.syncInfo} />
                </Box>
              )}
            </HStack>
          }
        >
          {!isEmpty(dayparts) && (
            <HStack
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              py={{ base: 1.5, xl: 2 }}
              px={{ base: 2, xl: 4 }}
              borderBottomWidth={1}
            >
              <HStack key={`week-dates.${locale}`} space={{ base: 1, xl: 2 }} alignItems="center" my={0.5}>
                <Button variant="unstyled" w={10} onPress={handleChangeWeek(-7)}>
                  <ArrowBackIcon size={4} color="gray.700" />
                </Button>

                <DateTimePopover
                  key={`date-picker-${week?.startTime.getTime()}`}
                  selectedDate={week?.startTime}
                  mode="week"
                  onChange={handleSelectWeek}
                  max={moment(maxDate).add(-1, 'day').toDate()}
                >
                  <Button variant="unstyled" startIcon={<FeatherIcons name="calendar" size={18} color="gray.700" />}>
                    {week
                      ? `Wk ${week.weekNumber} (${range(2)
                          .map(d =>
                            moment(week.startTime)
                              .add(6 * d, 'day')
                              .format('MMM DD'),
                          )
                          .join('- ')})`
                      : ''}
                  </Button>
                </DateTimePopover>

                <Button variant="unstyled" disabled={!week || week.endTime >= maxDate} w={10} onPress={handleChangeWeek(7)}>
                  <ArrowForwardIcon size={4} color="gray.700" />
                </Button>

                <Button
                  variant="unstyled"
                  display={{ base: 'none', xl: 'flex' }}
                  disabled={!week || week.endTime >= maxDate}
                  onPress={handleChangeWeek()}
                >
                  {t('app:current_week')}
                </Button>
              </HStack>

              {!!week && (
                <HStack my={0.5}>
                  <ButtonGroup key={locale} value={dateFocus} onChange={handleChangeDate}>
                    {range(7).map(day => {
                      const dt = moment(week?.startTime).add(day, 'day');
                      const startTime = siteTime.toStartEndOfPart(dt.toDate()).startTime;
                      const isoDate = formatToISOString(dt.toDate());
                      const isEnabled = startTime < siteTime.today();
                      return (
                        <ButtonGroup.Item
                          key={isoDate}
                          value={isoDate}
                          disabled={!isEnabled}
                          _text={{
                            color: !isEnabled ? 'gray.400' : isoDate === dateFocus ? 'black' : 'gray.600',
                          }}
                        >
                          {dt.format(isSmallScreen ? 'ddd' : 'ddd MM/DD')}
                        </ButtonGroup.Item>
                      );
                    })}
                  </ButtonGroup>
                </HStack>
              )}
            </HStack>
          )}

          <View flex={1}>
            <ResourceLoader
              h="full"
              isLoading={isEmpty(dayparts) || querySalesAndLabor.isLoading}
              isEmpty={querySalesAndLabor.data?.salesLaborData?.blocks?.length === 0}
            >
              <SalesTracker isDetailed={isDetailed.value} />
            </ResourceLoader>
          </View>
        </ScreenLayout>
      </SalesLaborTrackerProvider>
    </MainContainer>
  );
};
